// --- BTK safety: ensure ETA config exists (runs before we use it)
window.BTK_ETA = window.BTK_ETA || {
  cutoff: '14:00',        // fallback cutoff
  excludedDates: '',      // fallback excluded dates
  timezone: 'Europe/London'
};
(() => {
  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  // === Perf: image priority + lazy safety (improved) ===
  onReady(() => {
    try {
      const isPDP = document.body.classList.contains('template-product');

      // 1) Pick a single, meaningful LCP image
      let priorityImg = null;

      if (isPDP) {
        // Prefer the main PDP image (yours uses .o-img.o-img--cover)
        const pdpRoot =
          document.querySelector('[data-product-section], .product, .product-page, .product-template') ||
          document.querySelector('main');

        const pdpCandidates = pdpRoot
          ? pdpRoot.querySelectorAll(
              '.o-img.o-img--cover, .product__media img, .product-media img, .product-gallery img, [data-product-media] img, [data-media-id] img'
            )
          : document.querySelectorAll(
              '.o-img.o-img--cover, .product__media img, .product-media img, .product-gallery img, [data-product-media] img, [data-media-id] img'
            );

        for (const img of pdpCandidates) {
          const r = img.getBoundingClientRect();
          if (r.top >= 0 && r.top < window.innerHeight * 0.8 && r.width >= 400 && r.height >= 200) {
            priorityImg = img;
            break;
          }
        }
      }

      // Fallback: first large, above-the-fold image in <main>
      if (!priorityImg) {
        const candidates = document.querySelectorAll('main img');
        for (const img of candidates) {
          const r = img.getBoundingClientRect();
          if (r.top >= 0 && r.top < window.innerHeight * 0.75 && r.width >= 400 && r.height >= 200) {
            priorityImg = img;
            break;
          }
        }
      }

      if (priorityImg && !priorityImg.hasAttribute('fetchpriority')) {
        priorityImg.setAttribute('fetchpriority', 'high');
        priorityImg.setAttribute('loading', 'eager');
        priorityImg.setAttribute('decoding', 'async');
        // Help the browser choose the right srcset candidate if sizes is missing
        if (priorityImg.hasAttribute('srcset') && !priorityImg.hasAttribute('sizes')) {
          priorityImg.setAttribute('sizes', '100vw');
        }
      }

      // 2) Lazy-load all other images by default (skip header + priority)
      document.querySelectorAll('img:not([loading])').forEach((img) => {
        if (img === priorityImg) return;
        if (img.closest('header')) return;
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
      });

      // 3) Lazy-load iframes below the fold
      document.querySelectorAll('iframe:not([loading])').forEach((frame) => {
        const r = frame.getBoundingClientRect();
        if (r.top > window.innerHeight * 0.6) frame.setAttribute('loading', 'lazy');
      });
    } catch (e) {
      /* no-op */
    }
  });

  // === Sticky filters on collections (BM-free) ===
  onReady(() => {
    try {
      // Only on collection pages, desktop only
      if (!document.body.classList.contains('template-collection')) return;
      if (window.matchMedia('(max-width: 989px)').matches) return;

      // Find facets container
      const facetEl =
        document.querySelector('[data-facets]') ||
        document.querySelector('.facets') ||
        document.querySelector('.collection-facets') ||
        document.querySelector('.collection__filters');
      if (!facetEl) return;

      // If this collection uses a drawer/side panel for filters, skip sticky
      const usesDrawer =
        facetEl.querySelector('.drawer, .facets-drawer, [data-drawer], [data-drawer-target]') ||
        document.querySelector('[aria-controls*="Filter"], [aria-controls*="filter"]');
      if (usesDrawer) return;

      // Make it sticky (desktop horizontal facets only)
      facetEl.classList.add('btk-facets-sticky');

      // Compute dynamic top offset from header height
      const header =
        document.querySelector('.js-header-group') ||
        document.querySelector('header.header-group') ||
        document.querySelector('header');

      const computeTop = () => {
        const h = header ? Math.ceil(header.getBoundingClientRect().height) : 72;
        document.documentElement.style.setProperty('--btk-sticky-top', h + 'px');
      };
      computeTop();
      window.addEventListener('resize', computeTop, { passive: true });

      // Shadow when stuck
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:1px;margin-top:-1px;';
      facetEl.parentNode && facetEl.parentNode.insertBefore(sentinel, facetEl);

      if (typeof IntersectionObserver !== 'undefined') {
        const io = new IntersectionObserver(
          (entries) => {
            const e = entries[0];
            facetEl.classList.toggle('is-stuck', !e.isIntersecting);
          },
          {
            rootMargin: `-${getComputedStyle(document.documentElement)
              .getPropertyValue('--btk-sticky-top')
              .trim()} 0px 0px 0px`,
            threshold: 0
          }
        );
        io.observe(sentinel);
      }
    } catch (e) {
      /* no-op */
    }
  });
  // === Perf: idle-load third-party scripts that use data-src ===
  onReady(() => {
    const loadLazyScripts = () => {
      const lazy = document.querySelectorAll('script[data-src]');
      lazy.forEach((el) => {
        const url = el.getAttribute('data-src');
        if (!url) return;

        // Skip if already loaded elsewhere
        if (document.querySelector(`script[src="${url}"]`)) return;

        const s = document.createElement('script');
        // copy attributes except src/data-src
        for (const attr of el.attributes) {
          if (attr.name === 'src' || attr.name === 'data-src') continue;
          s.setAttribute(attr.name, attr.value);
        }
        s.src = url;
        // async by default; keep defer if original had it
        if (!s.hasAttribute('defer')) s.async = true;

        el.replaceWith(s);
      });
    };

    // Run after full load; if requestIdleCallback exists, be even nicer
    window.addEventListener('load', () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadLazyScripts, { timeout: 2000 });
      } else {
        setTimeout(loadLazyScripts, 500);
      }
    }, { once: true });
  });
})();
// === Biketart: collection grid image sizing & lazy (2 rows eager) ===
(() => {
  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  onReady(() => {
    if (!document.body.classList.contains('template-collection')) return;

    // Heuristic: images inside product tiles
    const candidates = Array.from(
      document.querySelectorAll(
        `
        a[href*="/products/"] img,
        .product-card img,
        .grid__item a img,
        .collection .product-item img
        `.replace(/\s+/g, ' ')
      )
    );
    if (!candidates.length) return;

    // Your layout: 5/4/3/2 per row
    const breakpoints = [
      { mq: '(min-width: 1280px)', perRow: 5 },
      { mq: '(min-width: 990px)',  perRow: 4 },
      { mq: '(min-width: 768px)',  perRow: 3 },
      { mq: '(min-width: 0px)',    perRow: 2 }
    ];
    const getPerRow = () => breakpoints.find(b => matchMedia(b.mq).matches).perRow;
    const perRow = getPerRow();
    const ABOVE_THE_FOLD_COUNT = perRow * 2; // 2 rows eager

    candidates.forEach((img, i) => {
      // Respect existing authoring
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', i < ABOVE_THE_FOLD_COUNT ? 'eager' : 'lazy');
      }

      // Sizes for 5/4/3/2 layout
      if (!img.hasAttribute('sizes')) {
        img.setAttribute(
          'sizes',
          '(min-width: 1280px) 20vw, (min-width: 990px) 25vw, (min-width: 768px) 33vw, 50vw'
        );
      }

      // Give just the very first image a small nudge if supported
      if (i === 0 && !img.hasAttribute('fetchpriority') && 'priority' in HTMLImageElement.prototype) {
        img.setAttribute('fetchpriority', 'high');
      }
    });
  });
})();
// === Collapse empty Bike Matrix app wrappers on collections ===
(() => {
  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  onReady(() => {
    if (!document.body.classList.contains('template-collection')) return;

    document.querySelectorAll('.shopify-section--app-wrapper').forEach((sec) => {
      // Case 1: Bike selector banner explicitly off
      const banner = sec.querySelector('bikematrix-bikeselectorbanner');
      if (banner && banner.getAttribute('data-show') === 'false') {
        sec.classList.add('btk-collapse');
        return;
      }

      // Case 2: Compatible list off via embedded JSON config
      const cfgEl = sec.querySelector('script[data-bikematrix-config]');
      if (cfgEl) {
        try {
          const cfg = JSON.parse(cfgEl.textContent || '{}');
          if (cfg.showCompatibleList === false) {
            sec.classList.add('btk-collapse');
            return;
          }
        } catch (e) {
          /* ignore */
        }
      }

      // Fallback: if wrapper has no visible content rendered, collapse it
      const hasVisible =
        sec.querySelector(':where(img, a, p, div, ul, ol, section):not([hidden])') &&
        sec.offsetHeight > 10; // ignore just-padding shells
      if (!hasVisible) sec.classList.add('btk-collapse');
    });
  });
})();
(() => {
  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  onReady(() => {
    // Locate the active product form on PDP
    const form =
      document.querySelector('product-form form') ||
      document.querySelector('form[action="/cart/add"]');
    if (!form) return;

    const msgSel = '.js-inventory-content'; // your visible stock message container

    const computeState = (text = '') => {
      const t = text.toLowerCase();
      if (t.includes('pre-order') || t.includes('preorder')) return 'preorder';
      if (t.includes('with supplier')) return 'supplier';
      if (t.includes('in stock')) return 'biketart';
      if (t.includes('out of stock')) return 'oos';
      return '';
    };

    const syncProps = () => {
      const msgEl = document.querySelector(msgSel);
      const msg = msgEl ? msgEl.textContent.trim() : '';
      const state = computeState(msg);
      form.querySelectorAll('.js-prop-stock-message').forEach(i => (i.value = msg));
      form.querySelectorAll('.js-prop-inventory-state').forEach(i => (i.value = state));
    };

    // Initial fill + update on option/variant changes
    syncProps();
    document.addEventListener('change', (e) => {
      if (e.target.closest('form') === form) syncProps();
    });
    document.addEventListener('variant:change', syncProps); // some themes emit this
  });
})();



